/**
 * Analytics Module
 * Main export file for all analytics utilities
 */

import { sendNewRelicEvent, sendNewRelicError } from '../newrelic';

import { ANALYTICS_EVENTS } from './constants';
import { cleanupPageTracker, initPageTracker } from './pageTracker';
import { cleanupSessionTracker, initSessionTracker } from './sessionTracker';

// Export constants
export * from './constants';

// Export page tracker
export {
  trackPageEntry,
  trackPageExit,
  getCurrentPageInfo,
  initPageTracker,
  cleanupPageTracker,
} from './pageTracker';

// Export session tracker
export {
  startSession,
  endSession,
  trackJourneyStep,
  trackFeatureUsage,
  getSessionInfo,
  getSessionId,
  initSessionTracker,
  cleanupSessionTracker,
} from './sessionTracker';

// Export SCS analytics
export * from './scsAnalytics';

// Export SLS analytics
export * from './slsAnalytics';

// Re-export base newrelic utilities for convenience
export {
  sendNewRelicEvent,
  sendNewRelicError,
  setNewRelicAttribute,
  isNewRelicEnabled,
} from '../newrelic';

/**
 * Initialize all analytics trackers
 * Call this once when the app starts
 */
export const initAnalytics = () => {
  initPageTracker();
  initSessionTracker();
  // eslint-disable-next-line no-console
  console.log('📊 Analytics initialized');
};

/**
 * Cleanup all analytics trackers
 * Call this when the app unmounts
 */
export const cleanupAnalytics = () => {
  cleanupPageTracker();
  cleanupSessionTracker();
};

/**
 * Track API latency
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {number} durationMs - Duration in milliseconds
 * @param {boolean} success - Whether the call succeeded
 * @param {object} context - Additional context
 */
export const trackApiLatency = (endpoint, method, durationMs, success, context = {}) => {
  sendNewRelicEvent('apiLatency', {
    durationMs,
    endpoint,
    method,
    success,
    ...context,
  });
};

/**
 * Track API error
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {number} statusCode - HTTP status code
 * @param {string} errorMessage - Error message
 * @param {object} context - Additional context
 */
export const trackApiError = (endpoint, method, statusCode, errorMessage, context = {}) => {
  sendNewRelicEvent(ANALYTICS_EVENTS.API_ERROR, {
    endpoint,
    errorMessage,
    method,
    statusCode,
    ...context,
  });

  sendNewRelicError(new Error(errorMessage), {
    endpoint,
    method,
    statusCode,
    ...context,
  });
};
